import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, CircularProgress
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import api from '../api/axios';

export default function AnnouncementOverlay() {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    fetchUnread();
  }, []);

  const fetchUnread = async () => {
    try {
      const { data } = await api.get('/announcements/unread/');
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch {
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRead = async () => {
    const current = announcements[currentIndex];
    if (!current) return;
    setMarking(true);
    try {
      await api.post(`/announcements/${current.id}/read/`);
      if (currentIndex + 1 < announcements.length) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setAnnouncements([]);
      }
    } catch {
      // Still proceed even if marking fails
      if (currentIndex + 1 < announcements.length) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setAnnouncements([]);
      }
    } finally {
      setMarking(false);
    }
  };

  if (loading || announcements.length === 0) return null;

  const current = announcements[currentIndex];
  const total = announcements.length;
  const position = currentIndex + 1;

  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.75)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
    }}>
      <Box sx={{
        background: 'white',
        borderRadius: 3,
        width: '100%',
        maxWidth: 560,
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <Box sx={{
          background: '#1a4d2e',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CampaignIcon sx={{ color: 'white', fontSize: 22 }} />
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: 15 }}>
              Announcement
            </Typography>
          </Box>
          {total > 1 && (
            <Box sx={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 10,
              px: 1.5,
              py: 0.5,
            }}>
              <Typography sx={{ color: 'white', fontSize: 12 }}>
                {position} of {total}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Body */}
        <Box sx={{ px: 3, py: 3 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 500, mb: 1.5, color: '#1a1a1a' }}>
            {current.title}
          </Typography>
          <Box sx={{
            maxHeight: 300,
            overflowY: 'auto',
            pr: 0.5,
          }}>
            <Typography sx={{
              fontSize: 14,
              color: '#444',
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap',
            }}>
              {current.body}
            </Typography>
          </Box>
          <Box sx={{
            mt: 2,
            pt: 2,
            borderTop: '0.5px solid #e8ede8',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <Typography sx={{ fontSize: 12, color: '#aaa' }}>
              From: {current.created_by_name}
              {current.created_by_role === 'department_head' ? ' (Dept. Head)' : ''}
              {current.created_by_role === 'admin' ? ' (Admin)' : ''}
              · {new Date(current.created_at).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{
          px: 3,
          py: 2,
          background: '#fafcfa',
          borderTop: '0.5px solid #e8ede8',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <Button
            variant="contained"
            onClick={handleRead}
            disabled={marking}
            sx={{
              background: '#1a4d2e',
              '&:hover': { background: '#2d7a4f' },
              borderRadius: 2,
              px: 3,
              fontSize: 13,
            }}
          >
            {marking
              ? <CircularProgress size={16} color="inherit" />
              : total > 1 && position < total
                ? `I have read this (${position}/${total})`
                : 'I have read this'
            }
          </Button>
        </Box>
      </Box>
    </Box>
  );
}